import { useEffect } from 'react';

import { SquarePenIcon, ZapIcon } from 'lucide-react';

import useFileSubmit from '@/components/atoms/FileUpload/useFileSubmit';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Spinner } from '@/components/ui/spinner';
import useFileUpload from '@/hooks/useFileUpload';

interface AvatarUploadProps {
  onUpdate: (avatar: { avatar?: string }) => void;
  name?: string;
  label: string;
  avatar?: string;
  freeForm?: boolean;
}

const AvatarUpload = ({ onUpdate, label, avatar, name, freeForm = false }: AvatarUploadProps) => {
  const { croppedFile, onAvatarUpload } = useFileUpload(freeForm ? 'free' : undefined);
  const { isLoading, uploadFile } = useFileSubmit({
    stream: true,
    onSuccess: (uploadedFile) => {
      if (uploadedFile?.url) {
        onUpdate({ avatar: uploadedFile.url });
      }
    },
  });

  useEffect(() => {
    if (croppedFile) {
      uploadFile(croppedFile);
    }
  }, [croppedFile, uploadFile]);

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center text-muted-foreground">
          <Spinner size="md" className="text-current" />
        </div>
      )}
      {avatar ? (
        <Avatar className="size-[72px]">
          <AvatarImage src={avatar} alt={name} className="object-cover" />
          <AvatarFallback>
            <ZapIcon size={35} className="text-muted-foreground" />
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className="flex size-[72px] items-center justify-center rounded-full bg-muted">
          <ZapIcon size={35} className="text-muted-foreground" />
        </div>
      )}
      <div className="absolute bottom-0 left-[54px] text-muted-foreground">
        <SquarePenIcon size={16} className="fill-background text-muted-foreground" />
      </div>
      <input
        type="file"
        accept=".png,.jpeg,.jpg"
        aria-label={label}
        onChange={onAvatarUpload}
        className="absolute -bottom-1 -left-1 size-20 cursor-pointer opacity-0 [&::-webkit-file-upload-button]:cursor-pointer"
      />
    </div>
  );
};

export default AvatarUpload;
